import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Stepper from '@material-ui/core/Stepper';
import StepperStep from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import StepContainer from 'components/@common/Stepper/StepContainer';
import StepDetails from 'components/@common/Stepper/StepDetails';
import StepActionBar from 'components/@common/Stepper/StepActionBar';
import StepSubheader from 'components/@common/Stepper/StepSubheader';
import StudentSelectTable, { StudentRowData } from './components/StudentSelectTable';
import { ModificationAlertContent } from '../../../../../hooks/sweet-alert/use-modification-alert';

import { Theme } from 'types';
import { useStepper } from 'hooks/use-stepper';

const useStyles = makeStyles((theme: Theme) => {
  const labelSize = '0.95rem';
  return {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    stepper: {
      height: theme.constants.modals.stepperHeight,
      margin: '0 auto',
      paddingLeft: '10%',
      paddingBottom: theme.spacing(4),
      width: '40%'
    },
    stepLabel: {
      fontSize: labelSize
    },
    stepLabelActive: {
      fontSize: labelSize,
      color: `${theme.palette.secondary.main} !important`
    },
    body: {
      height: `100%`,
      overflow: 'auto'
    },
    filterBarContainer: {
      textAlign: 'center',
      margin: 'auto 0',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1.5)
    },
    clearIcon: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: theme.spacing(1)
    },
    subheader: {
      maxWidth: '85%'
    }
  };
});

interface RemoveStudentModalStep {
  label: string;
}
const steps: RemoveStudentModalStep[] = [
  {
    label: 'SELECT STUDENTS'
  },
  {
    label: 'REVIEW & CONFIRM'
  }
];
export interface RemoveStudentModalProps {
  rowData: Array<StudentRowData>;
  onClose: () => void;
  onConfirm: (params: any, selected: Array<StudentRowData>) => void;
}

const initCheckedState = (studentData: Array<StudentRowData>): { [key: string]: boolean } =>
  studentData.reduce((acc, item: StudentRowData) => ({ ...acc, [`${item.id}`]: false }), {});

const RemoveStudentModal: React.FC<RemoveStudentModalProps> = ({ onClose, onConfirm, rowData }) => {
  const { t } = useTranslation('RemoveStudentModal');
  const classes = useStyles();
  const [{ activeStep }, { setNextStep }] = useStepper();
  const [studentCheckList, setStudentCheckList] = React.useState<{ [key: string]: boolean }>(
    initCheckedState(rowData)
  );
  const [confirmRowData, setConfirmRowData] = React.useState<StudentRowData[]>(rowData);

  const handleNext = (): void => {
    if (activeStep === 0) {
      setNextStep(1);
      setConfirmRowData(rowData.filter(({ id }: StudentRowData) => studentCheckList[id]));
    }
  };

  const handleConfirm = (): void => {
    onClose();
    const selectedStudents = rowData.filter(({ id }: StudentRowData) => studentCheckList[id]);
    onConfirm(
      {
        overrides: {
          html: (
            <ModificationAlertContent
              deleteText={t('Please type DELETE to confirm.')}
              textItems={selectedStudents.map(
                ({ firstName, lastName }: StudentRowData): string => `${firstName} ${lastName}`
              )}
            />
          )
        }
      },
      selectedStudents
    );
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target) {
      setStudentCheckList({
        ...studentCheckList,
        [event.target.value]: !studentCheckList[event.target.value]
      });
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <StepDetails variant={'title'}>{t('Delete Students')}</StepDetails>
        <Divider />
        <Container>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map(step => {
              return (
                <StepperStep key={step.label}>
                  <StepLabel
                    classes={{
                      active: classes.stepLabelActive,
                      label: classes.stepLabel
                    }}
                  >
                    <Box fontWeight={600}>{t(step.label)}</Box>
                  </StepLabel>
                </StepperStep>
              );
            })}
          </Stepper>
        </Container>
        <StepContainer value={activeStep} index={0}>
          <Container>
            <StepSubheader
              className={classes.subheader}
              title={`1. ${t('Delete Students')}`}
              subtitle={t('Select the students you would like to delete.')}
            />
            <StudentSelectTable
              checkMap={studentCheckList}
              data={rowData}
              handleCheck={handleCheck}
            />
          </Container>
        </StepContainer>
        <StepContainer value={activeStep} index={1}>
          <StepDetails>
            {t('Please review and confirm the students you have selected to be deleted.')}
          </StepDetails>
          <Container>
            <StudentSelectTable
              checkMap={studentCheckList}
              handleCheck={handleCheck}
              data={confirmRowData}
              readOnly={true}
            />
          </Container>
        </StepContainer>
      </div>
      <StepActionBar
        cancelText={t('Cancel')}
        confirmText={t(['Delete Students', 'Confirm'][activeStep])}
        disableConfirm={!Object.values(studentCheckList).find((value: boolean) => value)}
        onConfirm={activeStep === 0 ? handleNext : handleConfirm}
        onCancel={onClose}
      />
    </div>
  );
};
export default RemoveStudentModal;
